import React, { FC, memo, useRef } from 'react';
import classNames from 'classnames';
import { hasExternalForm } from '@/helpers/externalForm';
import { ModalName } from '@/common/contexts/globalModalContext';
import ListingLayout, { ListingLayoutProps } from '@/common/layouts/ListingLayout/ListingLayout';
import { PopupCardProps } from '@/pages/CareersPage/CareersPage.types';
import { WaterfallProps } from '@/pages/CareersPage/components/PageHeader/Waterfall/Waterfall';
import PageHeader from '@/pages/CareersPage/components/PageHeader/PageHeader';
import Video from '@/pages/CareersPage/components/Video/Video';
import PopupsBlock from '@/pages/CareersPage/components/PopupsBlock/PopupsBlock';
import Opportunities, { OpportunitiesProps } from '@/pages/CareersPage/components/Opportunities/Opportunities';
import ExternalModalForms from '@/common/components/Modal/ExternalModalForms/ExternalModalForms';
import grid from '@/common/css/grid.module.scss';
import styles from './CareersPage.module.scss';

interface CareersPageProps extends ListingLayoutProps, WaterfallProps, OpportunitiesProps {
  contentH2?: string;
  videoUrl?: string;
  popups?: PopupCardProps[];
  additionalCareerBlock?: {
    videoUrl?: string;
    text?: string;
  };
}

const customHeaderProps = {
  isInvertedByDefault: true,
};

const CareersPage: FC<CareersPageProps> = ({
  pageHeader,
  waterfallUrls,
  contentH2,
  videoUrl,
  opportunitiesLabel,
  jobs,
  popups,
  additionalCareerBlock,
  pardot,
  signUpSection,
  ...listingLayoutProps
}) => {
  const itemRefToScroll = useRef<HTMLDivElement | null>(null);

  const shouldRenderNewsletterModal = !hasExternalForm({ pardot, modalName: ModalName.newsletter });

  return (
    <ListingLayout
      contentClassName={styles.parallaxContentContainer}
      customHeaderProps={customHeaderProps}
      itemRefToScroll={itemRefToScroll}
      pageHeaderContent={<PageHeader {...pageHeader} waterfallUrls={waterfallUrls} />}
      shouldRenderNewsletterModal={shouldRenderNewsletterModal}
      signUpSection={signUpSection}
      {...listingLayoutProps}
    >
      {!!videoUrl && (
        <Video url={videoUrl} />
      )}
      <div className={grid.row}>
        <div className={classNames(grid.colSm10, grid.offsetSm1)}>
          <div className={styles.title}>
            {contentH2}
          </div>
          {!!additionalCareerBlock?.text && (
            <div
              className={styles.text}
              dangerouslySetInnerHTML={{ __html: additionalCareerBlock?.text }}
            />
          )}
        </div>
      </div>

      {!!popups && (
        <div className={styles.popupsBlock}>
          <PopupsBlock popups={popups} />
        </div>
      )}

      {!!jobs?.length && (
        <div ref={itemRefToScroll} className={grid.row}>
          <div>
            <Opportunities
              jobs={jobs}
              opportunitiesLabel={opportunitiesLabel}
            />
          </div>
        </div>
      )}

      {!!additionalCareerBlock?.videoUrl && (
        <Video url={additionalCareerBlock?.videoUrl} />
      )}

      {!!pardot && (
        <ExternalModalForms
          pardot={pardot}
          titles={{
            [ModalName.newsletter]: signUpSection?.modalWindowTitle,
          }}
        />
      )}
    </ListingLayout>
  );
};

export default memo(CareersPage);
